import ApiClient from '../../utils/api/ApiClient'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Subscription } from '../../types'

export enum BillErrorCode {
  NO_SUBSCRIPTION = 'NO_SUBSCRIPTION',
  SUBSCRIPTION_EXPIRED = 'SUBSCRIPTION_EXPIRED',
  NO_REMAINING_TOKENS = 'NO_REMAINING_TOKENS',
  EXCEED_SEAT_LIMIT = 'EXCEED_SEAT_LIMIT',
}

export enum BillWarningCode {
  LOW_REMAINING_TOKENS = 'LOW_REMAINING_TOKENS',
}

type BillingError = {
  code: BillErrorCode
}

type BillingWarning = {
  code: BillWarningCode
}

type BillingState = {
  subscription: {
    data?: Subscription
    error?: BillingError
    warning?: BillingWarning
    hasSentLowTokenNotification?: boolean
  }
}

type SubscriptionValidateResponse = {
  active: boolean
}

type SubscriptionValidateRequest = {
  organizationId: string
  prescriberId: string
}

type GetSubscriptionRequest = {
  organizationId: string
  prescriberId?: string
}

type UpdateSubscriptionRequest = {
  subscriptionId: string
  organizationId: string
  seats: number
  pricePlan: string
}

type GetInvoiceStatusRequest = {
  invoiceId: string
}

type GetPendingSubscriptionChangeRequest = {
  organizationId: string
}

interface CreateCheckoutSessionProps {
  pricePlan: string
  organizationId: string
  seats: number
}

interface CreateCustomerPortalSessionProps {
  stripeSubscriptionId: string
  organizationId: string
  returnUrl?: string
}

export const BillingClient = {
  createCheckoutSession: async ({
    pricePlan,
    organizationId,
    seats,
  }: CreateCheckoutSessionProps): Promise<string> => {
    const { data } = await ApiClient.post('bff/billing/checkout-session', {
      pricePlan,
      successfulUrl: `${process.env.REACT_APP_WEBSITE_BASE_URL}`,
      cancelUrl: `${process.env.REACT_APP_WEBSITE_BASE_URL}/price-plans`,
      organizationId,
      quantity: seats,
    })
    return data
  },
  updateSubscription: async ({
    subscriptionId,
    organizationId,
    seats,
    pricePlan,
  }: UpdateSubscriptionRequest) => {
    const { data } = await ApiClient.post('bff/billing/subscription/update', {
      subscriptionId,
      organizationId,
      seats,
      pricePlan,
    })
    return data
  },
  updateSubscriptionPreview: async ({
    subscriptionId,
    organizationId,
    seats,
    pricePlan,
  }: UpdateSubscriptionRequest) => {
    const { data } = await ApiClient.post('bff/billing/subscription/update-preview', {
      subscriptionId,
      organizationId,
      seats,
      pricePlan,
    })
    return data
  },
  getInvoiceStatus: async ({ invoiceId }: GetInvoiceStatusRequest) => {
    const query = new URLSearchParams({
      invoiceId,
    })
    const { data } = await ApiClient.get(
      `bff/billing/subscription/invoice-status?${query.toString()}`
    )
    return data
  },
  getPendingSubscriptionChange: async ({ organizationId }: GetPendingSubscriptionChangeRequest) => {
    const query = new URLSearchParams({
      organizationId,
    })
    const { data } = await ApiClient.get(
      `bff/billing/subscription/pending-change?${query.toString()}`
    )
    return data
  },
  createCustomerPortalSession: async ({
    stripeSubscriptionId,
    organizationId,
    returnUrl = '/settings?tab=users',
  }: CreateCustomerPortalSessionProps): Promise<string> => {
    const { data } = await ApiClient.post('bff/billing/customer-portal-session', {
      stripeSubscriptionId,
      returnUrl: `${process.env.REACT_APP_WEBSITE_BASE_URL}${returnUrl}`,
      organizationId,
    })
    return data
  },
  checkSubscriptionActive: async ({
    organizationId,
    prescriberId,
  }: SubscriptionValidateRequest): Promise<SubscriptionValidateResponse> => {
    const query = new URLSearchParams({
      organizationId,
      prescriberId,
    })
    const { data } = await ApiClient.get(`bff/billing/validate-subscription?${query.toString()}`)
    return data
  },
  getSubscription: createAsyncThunk(
    'billing/getSubscription',
    async (body: GetSubscriptionRequest, thunkAPI) => {
      try {
        const query = new URLSearchParams(body)
        const { data } = await ApiClient.get(`bff/billing/subscription?${query.toString()}`)
        return data as Subscription
      } catch (e: any) {
        if (!e?.response) {
          throw e
        }

        return thunkAPI.rejectWithValue(e.response.data)
      }
    }
  ),
}

const initialState: BillingState = {
  subscription: {
    data: undefined,
    error: undefined,
    warning: undefined,
    hasSentLowTokenNotification: false,
  },
}

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setHasSentLowTokenNotification: (state, { payload }: PayloadAction<boolean>) => {
      state.subscription.hasSentLowTokenNotification = payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(BillingClient.getSubscription.fulfilled, (state, { payload }) => {
        state.subscription.data = payload
        state.subscription.error = undefined
        if (payload.remainingTokens <= 0) {
          state.subscription.error = { code: BillErrorCode.NO_REMAINING_TOKENS }
        }
        if (payload.remainingTokens <= 3 && payload.remainingTokens > 0) {
          state.subscription.warning = { code: BillWarningCode.LOW_REMAINING_TOKENS }
        }
        if (payload.hasEnoughSeats === false) {
          state.subscription.error = { code: BillErrorCode.EXCEED_SEAT_LIMIT }
        }
      })
      .addCase(BillingClient.getSubscription.rejected, (state, { payload }) => {
        state.subscription.data = undefined
        state.subscription.error = { code: (payload as BillingError)?.code }
      })
  },
})

export const { setHasSentLowTokenNotification } = billingSlice.actions
export default billingSlice.reducer
